import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Code, Copy, Check, ExternalLink, BarChart3, ArrowLeft, Eye, MousePointerClick, ShoppingCart } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

/**
 * Hotel embed widget configurator.
 *
 * Three variants × five themes. Live preview is an iframe loading
 * a tiny harness page that itself loads /widget.js — this matches
 * exactly what visitors of the hotel's own site will see.
 */
const VARIANTS = [
    { key: "box", label: "Box", desc: "Dikey kart, sidebar/sticky için" },
    { key: "bar", label: "Bar", desc: "Tek satır, sayfa üstünde" },
    { key: "full", label: "Full", desc: "Geniş hero + form, kendi sayfasında" },
    { key: "rooms", label: "Rooms", desc: "Oda kataloğu önde, sabit tarihlerle" },
    { key: "calendar", label: "Calendar", desc: "Aylık takvim ızgarası — gün seç → oda seç" },
];

const THEMES = [
    { key: "modern-sky", label: "Modern Sky", color: "#0ea5e9", bg: "#ffffff" },
    { key: "elegant-dark", label: "Elegant Dark", color: "#fbbf24", bg: "#0f172a" },
    { key: "coastal", label: "Coastal", color: "#14b8a6", bg: "#f0fdfa" },
    { key: "boutique-rose", label: "Boutique Rose", color: "#e11d48", bg: "#fff7ed" },
    { key: "minimal-mono", label: "Minimal Mono", color: "#000000", bg: "#ffffff" },
];

export default function AdminHotelWidgetPage() {
    const { id } = useParams();
    const [hotel, setHotel] = useState(null);
    const [variant, setVariant] = useState("box");
    const [theme, setTheme] = useState("modern-sky");
    const [lang, setLang] = useState("tr");
    const [analytics, setAnalytics] = useState(null);
    const [copied, setCopied] = useState(false);

    // Origin: same as where the API/frontend is hosted. The widget.js is served from /widget.js.
    const origin = typeof window !== "undefined" ? window.location.origin : "";

    useEffect(() => {
        api.get(`/admin/hotels/${id}`).then((r) => setHotel(r.data)).catch(() => setHotel(null));
    }, [id]);

    const loadAnalytics = useCallback(() => {
        api.get(`/admin/hotels/${id}/widget-analytics`).then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));
    }, [id]);

    useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

    const embedCode = useMemo(() => (
        `<!-- msonlinetravel rezervasyon widget'ı -->\n` +
        `<div id="msonline-booking"\n` +
        `     data-hotel-id="${id}"\n` +
        `     data-variant="${variant}"\n` +
        `     data-theme="${theme}"\n` +
        `     data-lang="${lang}"></div>\n` +
        `<script src="${origin}/widget.js" async></script>`
    ), [id, variant, theme, lang, origin]);

    const previewSrcDoc = useMemo(() => {
        // A blank harness page that loads the widget script — exactly like a hotel site
        return `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<style>
  html,body { margin:0; padding:0; background: #f1f5f9; min-height: 100%; }
  body { padding: 28px; display: flex; align-items: flex-start; justify-content: center; }
  .wrap { width: 100%; max-width: ${variant === "bar" ? "880px" : variant === "full" ? "520px" : (variant === "rooms" || variant === "calendar") ? "440px" : "320px"}; }
</style>
</head><body>
  <div class="wrap">
    <div id="msonline-booking"
         data-hotel-id="${id}"
         data-variant="${variant}"
         data-theme="${theme}"
         data-lang="${lang}"></div>
  </div>
  <script src="${origin}/widget.js"></script>
</body></html>`;
    }, [id, variant, theme, lang, origin]);

    const copyCode = async () => {
        try { await navigator.clipboard.writeText(embedCode); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) {}
    };

    return (
        <AdminLayout title={`${hotel?.name || "Otel"} — Widget`}
            action={
                <Link to={`/admin/hotels/${id}/edit`} className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 dark:text-slate-300 hover:text-sky-600 px-2 py-1 rounded-lg">
                    <ArrowLeft className="h-3.5 w-3.5" /> Otel Bilgileri
                </Link>
            }
        >
            <div className="space-y-5 max-w-7xl">
                {/* Intro */}
                <div className="rounded-2xl bg-gradient-to-br from-sky-50 via-indigo-50 to-emerald-50 dark:from-sky-950/40 dark:via-indigo-950/30 dark:to-emerald-950/30 border-2 border-sky-200 dark:border-sky-900 p-5">
                    <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-sky-500 text-white flex items-center justify-center"><Code className="h-5 w-5" /></div>
                        <div className="flex-1">
                            <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">Web Sitesine Widget Gömme</h2>
                            <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                                Otelin kendi web sitesine yapıştırılacak rezervasyon kutusu. Misafirler buradan arama yapınca msonlinetravel'a yönlendirilir; rezervasyon <b>aynı şekilde Rezervasyonlar sekmesine</b> kaynak: <code>widget</code> olarak düşer.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Analytics */}
                {analytics && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="widget-analytics">
                        <AnalyticCard icon={<Eye className="h-4 w-4" />} label="Görüntülenme" value={analytics.views} color="sky" />
                        <AnalyticCard icon={<MousePointerClick className="h-4 w-4" />} label="Tıklama" value={analytics.clicks} color="indigo" />
                        <AnalyticCard icon={<ShoppingCart className="h-4 w-4" />} label="Rezervasyon" value={analytics.bookings} color="emerald" />
                        <AnalyticCard icon={<BarChart3 className="h-4 w-4" />} label="CTR" value={`%${analytics.ctr}`} color="amber" />
                        <AnalyticCard icon={<BarChart3 className="h-4 w-4" />} label="Dönüşüm" value={`%${analytics.conv}`} color="rose" />
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5">
                    {/* Configurator */}
                    <div className="space-y-4">
                        {/* Variant */}
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Boyut</div>
                            <div className="space-y-1.5">
                                {VARIANTS.map((v) => (
                                    <button
                                        key={v.key}
                                        onClick={() => setVariant(v.key)}
                                        data-testid={`variant-${v.key}`}
                                        className={`w-full text-left px-3 py-2 rounded-xl border-2 transition-colors ${variant === v.key ? "border-sky-500 bg-sky-50/60 dark:bg-sky-950/20" : "border-slate-200 dark:border-slate-700 hover:border-sky-300"}`}
                                    >
                                        <div className="text-sm font-bold text-slate-900 dark:text-white">{v.label}</div>
                                        <div className="text-[11px] text-slate-500">{v.desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Theme */}
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Tema</div>
                            <div className="grid grid-cols-2 gap-2">
                                {THEMES.map((t) => (
                                    <button
                                        key={t.key}
                                        onClick={() => setTheme(t.key)}
                                        data-testid={`theme-${t.key}`}
                                        className={`relative p-2 rounded-xl border-2 transition-colors ${theme === t.key ? "border-sky-500 ring-2 ring-sky-200 dark:ring-sky-900" : "border-slate-200 dark:border-slate-700"}`}
                                        style={{ background: t.bg }}
                                    >
                                        <div className="h-6 rounded mb-1" style={{ background: t.color }} />
                                        <div className="text-[11px] font-bold" style={{ color: t.key === "elegant-dark" ? "#f8fafc" : "#0f172a" }}>{t.label}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Lang */}
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Dil</div>
                            <div className="flex gap-2">
                                {["tr", "en"].map((l) => (
                                    <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`} className={`flex-1 py-2 rounded-lg text-xs font-bold ${lang === l ? "bg-sky-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"}`}>
                                        {l.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Preview + Code */}
                    <div className="space-y-4">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 dark:border-slate-800">
                                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Canlı Önizleme</div>
                                <div className="text-[10px] text-slate-400">Bu görünüm otelin sitesinde tam olarak böyle gözükecek</div>
                            </div>
                            <div className="bg-slate-100 dark:bg-slate-900/60">
                                <iframe
                                    title="widget-preview"
                                    srcDoc={previewSrcDoc}
                                    sandbox="allow-scripts allow-popups allow-same-origin"
                                    style={{ width: "100%", height: variant === "full" ? "640px" : variant === "bar" ? "240px" : "440px", border: 0 }}
                                    data-testid="widget-preview-iframe"
                                />
                            </div>
                        </div>

                        {/* Code */}
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Gömme Kodu</div>
                                <button onClick={copyCode} data-testid="copy-embed-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold">
                                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                                    {copied ? "Kopyalandı!" : "Kodu Kopyala"}
                                </button>
                            </div>
                            <pre data-testid="embed-code" className="bg-slate-50 dark:bg-slate-950 rounded-xl p-3 text-[11px] text-slate-700 dark:text-slate-200 overflow-x-auto whitespace-pre-wrap break-all border border-slate-200 dark:border-slate-800">{embedCode}</pre>
                            <p className="text-[11px] text-slate-500 mt-2">
                                Otele iletin: bu kodu kendi sitesinin istediği yerine (HTML kaynak/widget alanı/iframe alanı) yapıştırması yeterli. Tarayıcı önbelleği temizlendiğinde son ayarları yansıtır.
                            </p>
                            <a href={`/hotel/${id}?ref=widget&wv=${variant}&wt=${theme}&rh=${id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-xs font-bold text-sky-600 hover:underline">
                                Hedef sayfayı aç <ExternalLink className="h-3 w-3" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}

function AnalyticCard({ icon, label, value, color }) {
    const palette = {
        sky: "from-sky-50 to-sky-100 dark:from-sky-950/30 dark:to-sky-900/20 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-900",
        indigo: "from-indigo-50 to-indigo-100 dark:from-indigo-950/30 dark:to-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-900",
        emerald: "from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900",
        amber: "from-amber-50 to-amber-100 dark:from-amber-950/30 dark:to-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900",
        rose: "from-rose-50 to-rose-100 dark:from-rose-950/30 dark:to-rose-900/20 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-900",
    }[color];
    return (
        <div className={`p-3 rounded-xl bg-gradient-to-br ${palette} border`}>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-80">{icon}{label}</div>
            <div className="text-2xl font-display font-bold mt-1">{value}</div>
        </div>
    );
}
